import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, CalendarClock, ClipboardCheck, FileText, HeartPulse, Layers, ListChecks, LockKeyhole, MessageSquareText, Pill, ScrollText, ShieldCheck, Stethoscope, UserRound, Users } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/marketing/Reveal";
import { Label, ctaPrimary, ctaSecondary, h2Class } from "@/components/marketing/MarketingPrimitives";

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const CHAIN = [
  { icon: UserRound, title: "Patient", text: "signale, déclare, transmet depuis son téléphone" },
  { icon: Layers, title: "Informations structurées", text: "symptômes gradés, traitements, examens, documents, rendez-vous, événements" },
  { icon: Users, title: "IDEC / Coordination", text: "une vue par patient et une liste du jour par équipe" },
  { icon: ListChecks, title: "Priorisation", text: "un même problème = une alerte, avec motif, heure et responsable" },
  { icon: Stethoscope, title: "Équipe médicale", text: "informée avec le contexte et l’historique" },
  { icon: ClipboardCheck, title: "Décision", text: "médicale, tracée, appliquée jusqu’au patient" },
];

export function HeroChainVisual() {
  const reduce = useReducedMotion();
  return (
    <div className="relative mx-auto w-full max-w-xl" data-testid="hero-product-visual">
      <div className="rounded-[2rem] border border-emerald-100 bg-white p-4 shadow-[0_28px_90px_rgba(15,23,42,0.10)]">
        <div className="rounded-[1.5rem] border border-slate-100 bg-slate-50/70 p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-slate-400">Chaîne de coordination</p>
            <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-extrabold text-teal-700">Chaque étape tracée</span>
          </div>
          <ol className="relative mt-5 space-y-2.5">
            <span className="absolute bottom-6 left-[2.1rem] top-6 w-px bg-emerald-200" aria-hidden="true" />
            {CHAIN.map(({ icon: Icon, title, text }, i) => (
              <motion.li key={title} initial={reduce ? false : { opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 + i * 0.12, duration: 0.45 }} className="relative flex items-center gap-4 rounded-xl border border-slate-100 bg-white px-4 py-3" data-testid={`hero-chain-step-${i + 1}`}>
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${i === CHAIN.length - 1 ? "bg-slate-800" : "bg-emerald-600"}`}><Icon className="h-4 w-4" /></span>
                <div><p className="text-sm font-extrabold text-slate-800">{title}</p><p className="text-xs font-medium text-slate-500">{text}</p></div>
              </motion.li>
            ))}
          </ol>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="hero-chain-actors">
            {["Établissement", "Secrétariat", "Laboratoire d’analyses"].map((a) => <span key={a} className="rounded-full border border-emerald-100 bg-white px-3 py-1 text-xs font-bold text-slate-500">{a}</span>)}
          </div>
        </div>
      </div>
      <div className="absolute -bottom-8 -left-4 hidden max-w-xs rounded-2xl border border-emerald-100 bg-white p-4 shadow-xl md:block" data-testid="hero-floating-human-card">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-500">Infirmière de coordination</p>
        <p className="mt-2 text-sm font-bold text-slate-800">Elle pilote le suivi. Le médecin décide.</p>
      </div>
    </div>
  );
}

const PROBLEM_CARDS = [
  [UserRound, "Pour le patient", "Des informations à répéter, des questions sans interlocuteur identifié."],
  [Users, "Pour l’IDEC", "Des signaux dispersés, impossibles à prioriser et à tracer."],
  [Building2, "Pour l’équipe médicale et l’établissement", "Des décisions prises sans vue d’ensemble, aucun indicateur de parcours."],
];

export function ProblemSection() {
  return (
    <section id="parcours" className="scroll-mt-20 border-y border-emerald-100 bg-white px-5 py-20 lg:px-8 lg:py-24" data-testid="home-problem-section">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <Label>Le constat</Label>
          <h2 className={`${h2Class} max-w-3xl`} data-testid="home-problem-title">Une grande partie du parcours se joue entre deux venues.</h2>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-500" data-testid="home-problem-text">Symptômes, effets indésirables, traitements oraux, examens, documents, rendez-vous, événements cliniques : ce qui influence la prise en charge survient souvent hors de l’établissement. Aujourd’hui, ces informations circulent par appels, messages, pièces jointes et logiciels qui ne se parlent pas. Chaque acteur n’en voit qu’une partie.</p>
        </Reveal>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-3" testId="home-problem-cards">
          {PROBLEM_CARDS.map(([Icon, title, text]) => (
            <StaggerItem key={title} className="rounded-2xl border border-emerald-100 bg-slate-50 p-6" testId={`home-problem-card-${slug(title)}`}>
              <Icon className="h-6 w-6 text-teal-500" />
              <h3 className="mt-4 text-lg font-extrabold text-slate-800">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">{text}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function ResponseSection() {
  return (
    <section id="comment-ca-marche" className="scroll-mt-20 px-5 py-20 lg:px-8 lg:py-24" data-testid="home-how-section">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <Label>La réponse HANA</Label>
          <h2 className={`${h2Class} max-w-3xl`} data-testid="home-how-title">Une continuité numérique structurée autour du patient.</h2>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-500" data-testid="home-how-text">HANA recueille les informations du parcours, les structure, les priorise et les transmet au bon professionnel, au bon moment. Chaque étape est tracée. Chaque acteur voit ce qui relève de son rôle.</p>
        </Reveal>
        <Stagger className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3" testId="home-how-steps">
          {CHAIN.map(({ icon: Icon, title, text }, i) => (
            <StaggerItem key={title} className="relative rounded-[1.5rem] border border-emerald-100 bg-white p-6 shadow-sm" testId={`home-how-step-${i + 1}`}>
              <div className="flex items-center justify-between">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl text-white ${i === CHAIN.length - 1 ? "bg-slate-800" : "bg-emerald-600"}`}><Icon className="h-5 w-5" /></span>
                <span className="font-display text-3xl font-bold text-emerald-200">{i + 1}</span>
              </div>
              <h3 className="mt-5 text-lg font-extrabold text-slate-800">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">{text}</p>
              {i < CHAIN.length - 1 && <ArrowRight className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-emerald-300 lg:block" aria-hidden="true" />}
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.1}>
          <blockquote className="mt-12 rounded-[1.75rem] border-l-4 border-emerald-600 bg-emerald-50 px-8 py-6 font-display text-2xl font-bold leading-snug text-slate-800 sm:text-3xl" data-testid="home-pivot-quote">HANA structure le suivi. La clinique garde la décision médicale.</blockquote>
        </Reveal>
      </div>
    </section>
  );
}

const CARE_POINTS = [
  [HeartPulse, "Symptômes et effets indésirables gradés"],
  [Pill, "Traitement oral : rappels et prises"],
  [MessageSquareText, "Questionnaires de suivi"],
  [FileText, "Examens et documents"],
  [CalendarClock, "Rendez-vous et étapes du parcours"],
  [Users, "Lien avec l’infirmière de coordination"],
];

export function CareSection() {
  return (
    <section id="soigner" className="scroll-mt-20 border-y border-emerald-100 bg-slate-50 px-5 py-20 lg:px-8 lg:py-24" data-testid="home-care-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
        <Reveal>
          <Label>Soigner</Label>
          <h2 className={h2Class} data-testid="home-care-title">Le patient reste intégré à son parcours, même hors de l’établissement.</h2>
          <p className="mt-6 text-lg leading-8 text-slate-500" data-testid="home-care-text">Depuis une application simple, le patient signale ses symptômes, suit son traitement oral, répond aux questionnaires, transmet ses examens et retrouve ses rendez-vous et ses documents. Ses informations rejoignent son parcours au lieu de se perdre.</p>
          <Link to="/patients" className={`${ctaSecondary} mt-8`} data-testid="home-care-patients-link">HANA pour les patients <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
        <Stagger className="grid gap-3 sm:grid-cols-2" testId="home-care-points">
          {CARE_POINTS.map(([Icon, text]) => (
            <StaggerItem key={text} className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-white px-4 py-4" testId={`home-care-point-${slug(text)}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Icon className="h-4 w-4" /></span>
              <p className="text-sm font-bold text-slate-700">{text}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function CoordinateSection({ children }) {
  return (
    <section id="coordonner" className="scroll-mt-20 px-5 py-20 lg:px-8 lg:py-24" data-testid="home-coordinate-section">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <Label>Coordonner</Label>
          <h2 className={`${h2Class} max-w-3xl`} data-testid="home-coordinate-title">Une vision structurée des patients pour les IDEC et les équipes.</h2>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-500" data-testid="home-coordinate-text">HANA identifie les situations qui nécessitent une intervention, organise les relances, suit les étapes du parcours et transmet la bonne information au bon professionnel. L’infirmière de coordination pilote ; le médecin décide.</p>
        </Reveal>
        <div className="mt-16">{children}</div>
        <Reveal><div className="mt-16 flex justify-center"><Link to="/professionnels" className={ctaPrimary} data-testid="home-coordinate-establishments-link">HANA pour votre établissement <ArrowRight className="h-4 w-4" /></Link></div></Reveal>
      </div>
    </section>
  );
}

const UNDERSTAND_CARDS = [
  [Building2, "Pour l’établissement", "Indicateurs de parcours et de coordination : délais, alertes, observance, événements, charge de coordination."],
  [ScrollText, "Pour la recherche et les partenaires", "Données agrégées, pseudonymisation forte avec techniques d’anonymisation, finalités encadrées."],
  [ShieldCheck, "Toujours", "Le soin d’abord ; la donnée est une conséquence, jamais une condition."],
];

export function UnderstandSection() {
  return (
    <section id="comprendre" className="scroll-mt-20 border-y border-emerald-100 bg-white px-5 py-20 lg:px-8 lg:py-24" data-testid="home-understand-section">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <Label>Comprendre</Label>
          <h2 className={`${h2Class} max-w-3xl`} data-testid="home-understand-title">Un parcours structuré devient un parcours mesurable.</h2>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-500" data-testid="home-understand-text">Parce que chaque étape est tracée, l’établissement peut suivre ses parcours : délais, alertes, observance, événements, charge de coordination. Lorsque le cadre réglementaire et les consentements le permettent, ces données peuvent produire des indicateurs de vie réelle gouvernés, agrégés et pseudonymisés.</p>
        </Reveal>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-3" testId="home-understand-cards">
          {UNDERSTAND_CARDS.map(([Icon, title, text]) => (
            <StaggerItem key={title} className="rounded-2xl border border-emerald-100 bg-slate-50 p-6" testId={`home-understand-card-${slug(title)}`}>
              <Icon className="h-6 w-6 text-teal-500" />
              <h3 className="mt-4 text-lg font-extrabold text-slate-800">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">{text}</p>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal><Link to="/pharma" className={`${ctaSecondary} mt-10`} data-testid="home-understand-partners-link">Partenaires & données <ArrowRight className="h-4 w-4" /></Link></Reveal>
      </div>
    </section>
  );
}

export function EcosystemSection() {
  const today = ["Patient", "Infirmière de coordination", "Oncologue", "Secrétariat", "Direction d’établissement"];
  const next = ["Laboratoires d’analyses", "Pharmacie", "Autres spécialités", "Structures de proximité"];
  return (
    <section id="ecosysteme" className="scroll-mt-20 px-5 py-20 lg:px-8 lg:py-24" data-testid="home-ecosystem-section">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_1fr] lg:items-start">
        <Reveal>
          <Label>Un parcours, plusieurs acteurs</Label>
          <h2 className={h2Class} data-testid="home-ecosystem-title">HANA relie progressivement les acteurs du parcours.</h2>
          <p className="mt-6 text-lg leading-8 text-slate-500" data-testid="home-ecosystem-text">Une même infrastructure, des rôles distincts : chaque acteur accède à ce qui relève de sa fonction, et l’information suit le patient d’un acteur à l’autre.</p>
          <p className="mt-6 rounded-2xl border border-emerald-100 bg-slate-50 p-5 text-sm font-semibold leading-6 text-slate-600" data-testid="home-ecosystem-horizon">Nous commençons par l’oncologie, où la coordination est la plus exigeante. L’architecture est conçue pour d’autres parcours longs — cardiologie, chirurgie ambulatoire, maladies chroniques — lorsque les équipes en exprimeront le besoin.</p>
        </Reveal>
        <Reveal delay={0.1} className="grid gap-5">
          <div className="rounded-[1.75rem] border border-emerald-100 bg-white p-6 shadow-sm" data-testid="home-ecosystem-today">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-emerald-700">Aujourd’hui</p>
            <div className="mt-4 flex flex-wrap gap-2">{today.map((a) => <span key={a} className="rounded-full bg-emerald-50 px-3.5 py-1.5 text-sm font-bold text-emerald-800">{a}</span>)}</div>
          </div>
          <div className="rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-6" data-testid="home-ecosystem-next">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-slate-500">Progressivement</p>
            <div className="mt-4 flex flex-wrap gap-2">{next.map((a) => <span key={a} className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-bold text-slate-600">{a}</span>)}</div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export const HANA_HANDLES = ["Accès du patient", "Recueil structuré", "Tri et priorisation", "Rappels et relances", "Suivi des étapes", "Traçabilité", "Synthèses"];
export const CLINIC_KEEPS = ["Décision médicale", "Prescription", "Choix du traitement", "Protocoles internes", "Annonce au patient", "Responsabilité du soin"];

export function WhoDoesWhat({ testId = "home-responsibility-section" }) {
  return (
    <section className="px-5 pb-20 lg:px-8" data-testid={testId}>
      <Reveal>
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-emerald-100 bg-white p-8 md:p-12">
          <Label>Qui fait quoi</Label>
          <h2 className="max-w-3xl font-display text-3xl font-bold leading-tight text-slate-800 sm:text-4xl" data-testid={`${testId}-title`}>HANA structure le suivi. Votre établissement garde la décision médicale.</h2>
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div data-testid="professional-hana-handles-card">
              <h3 className="text-xl font-extrabold text-emerald-700">HANA prend en charge</h3>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">{HANA_HANDLES.map((item) => <div key={item} className="rounded-xl bg-emerald-50 p-3 text-sm font-bold text-slate-700">{item}</div>)}</div>
            </div>
            <div data-testid="professional-clinic-keeps-card">
              <h3 className="text-xl font-extrabold text-slate-800">L’établissement conserve</h3>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">{CLINIC_KEEPS.map((item) => <div key={item} className="rounded-xl bg-slate-50 p-3 text-sm font-bold text-slate-700">{item}</div>)}</div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export const TRUST_POINTS = [
  [Users, "Rôles séparés", "Chacun voit ce qui relève de sa fonction."],
  [ScrollText, "Consentement", "Recueilli, daté, révocable ; la recherche est un choix distinct du soin."],
  [ListChecks, "Traçabilité", "Qui, quoi, quand, pour chaque action clé."],
  [LockKeyhole, "Pseudonymisation forte", "Avec techniques d’anonymisation pour tout usage secondaire."],
  [ShieldCheck, "Conformité CNDP", "Traitements déclarés, finalités définies."],
  [Stethoscope, "Aucune décision automatique", "HANA trie et présente ; il n’interprète pas."],
];

export function TrustSection() {
  return (
    <section id="confiance" className="scroll-mt-20 border-y border-emerald-100 bg-slate-50 px-5 py-20 lg:px-8 lg:py-24" data-testid="home-trust-section">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <Label>Confiance</Label>
          <h2 className={`${h2Class} max-w-3xl`} data-testid="home-trust-title">Des données de santé protégées, une responsabilité médicale claire.</h2>
        </Reveal>
        <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" testId="home-trust-points">
          {TRUST_POINTS.map(([Icon, title, text]) => (
            <StaggerItem key={title} className="flex items-start gap-4 rounded-2xl border border-emerald-100 bg-white p-5" testId={`home-trust-point-${slug(title)}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><Icon className="h-5 w-5" /></span>
              <div><p className="text-base font-extrabold text-slate-800">{title}</p><p className="mt-1 text-sm leading-6 text-slate-500">{text}</p></div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal><Link to="/securite" className={`${ctaSecondary} mt-10`} data-testid="home-trust-security-link">Sécurité et données <ArrowRight className="h-4 w-4" /></Link></Reveal>
      </div>
    </section>
  );
}

const REFERENCES = [
  ["Moins de passages aux urgences", "Dans l’étude PRO-TECT, les patients qui signalaient leurs symptômes en ligne ont eu moins de visites aux urgences à 12 mois : 48,7 % contre 54,8 %.", "Basch et al., JAMA 2022"],
  ["Meilleure qualité de vie", "Le signalement des symptômes par le patient améliore leur contrôle, la forme physique et la qualité de vie.", "Basch et al., JAMA 2022"],
  ["Recommandations", "ASCO et ESMO recommandent d’intégrer les symptômes rapportés par le patient dans le suivi, avec alertes et réponse organisée.", "ASCO / ESMO"],
  ["Rôle de l’infirmière", "Associer un outil numérique à un suivi infirmier est particulièrement efficace pour les traitements oraux.", "Oncology Nursing Society"],
];

export function ReferencesSection({ testId = "professional-references-section" }) {
  return (
    <section className="px-5 pb-20 lg:px-8" data-testid={testId}>
      <Reveal>
        <div className="mx-auto max-w-7xl rounded-[2rem] bg-slate-900 p-8 text-white md:p-12">
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-teal-300">Pourquoi ça marche</p>
          <h2 className="mt-4 max-w-4xl font-display text-3xl font-bold leading-tight md:text-4xl">Ce que montrent les études.</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {REFERENCES.map(([title, text, source]) => (
              <article key={title} className="rounded-2xl border border-white/10 bg-white/[0.06] p-5" data-testid={`reference-card-${slug(title)}`}>
                <p className="font-extrabold text-teal-200">{title}</p>
                <p className="mt-3 text-sm leading-6 text-slate-200">{text}</p>
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.16em] text-teal-300">{source}</p>
              </article>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

const VALUE_LEVELS = [
  ["Soigner", "Le patient reste intégré à son parcours, même hors de l’établissement.", "/#soigner"],
  ["Coordonner", "Une vision structurée des patients pour les IDEC et les équipes : priorisation, relances, étapes, transmission.", "/#coordonner"],
  ["Comprendre", "Des indicateurs de parcours pour la direction ; une donnée de vie réelle gouvernée, lorsque le cadre le permet.", "/#comprendre"],
];

export function ValueLevelsCompact() {
  return (
    <section className="px-5 pb-20 lg:px-8" data-testid="value-levels-section">
      <Stagger className="mx-auto grid max-w-7xl gap-5 md:grid-cols-3" testId="value-levels-grid">
        {VALUE_LEVELS.map(([title, text, to], i) => (
          <StaggerItem key={title} className={`rounded-[1.75rem] border p-7 ${i === 1 ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-100 bg-white"}`} testId={`value-level-${slug(title)}`}>
            <p className={`text-xs font-extrabold uppercase tracking-[0.22em] ${i === 1 ? "text-teal-100" : "text-teal-500"}`}>{title}</p>
            <p className={`mt-4 text-lg font-extrabold leading-snug ${i === 1 ? "text-white" : "text-slate-800"}`}>{text}</p>
            <Link to={to} className={`mt-5 inline-flex items-center gap-2 text-sm font-bold ${i === 1 ? "text-white" : "text-emerald-700"}`} data-testid={`value-level-link-${slug(title)}`}>Voir sur le parcours <ArrowRight className="h-4 w-4" /></Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}
